import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
    Briefcase, ChevronDown, ChevronRight, Mail, Trash2, X, Check, Clock,
    ArrowLeft, Lock, LockOpen,
} from 'lucide-react';

import { api } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.jsx';
import './DealsView.css';

/* ==========================================================================
 * buildMailtoUrl — compose a mailto: link using the startup's email template.
 *
 * Placeholders supported in both subject and body:
 *   {{contact_first_name}}  — first word of the primary contact name
 *   {{contact_full_name}}   — full primary contact name
 *   {{investor_name}}       — fund / investor name
 *   {{my_name}}             — logged-in user's display name
 *
 * If the startup has neither email_subject nor email_body, returns a bare
 * `mailto:email` with no params so the client's default compose window opens.
 * ========================================================================== */
function buildMailtoUrl(deal, startup, currentUser) {
    const email = deal.primary_contact_email || '';

    if (!startup?.email_subject && !startup?.email_body) {
        return `mailto:${email}`;
    }

    const contactName  = deal.primary_contact_name || '';
    const firstName    = contactName.trim().split(/\s+/)[0] || '';
    const investorName = deal.investor_name || '';
    const myName       = currentUser?.name || '';

    const substitute = (template) => {
        if (!template) return '';
        let result = template
            .replace(/\{\{contact_first_name\}\}/g, firstName)
            .replace(/\{\{contact_full_name\}\}/g,  contactName)
            .replace(/\{\{investor_name\}\}/g,       investorName)
            .replace(/\{\{my_name\}\}/g,             myName);
        // Clean up artifacts from empty substitutions:
        // e.g. "Hi ,  how" → "Hi, how", "Dear  John" → "Dear John"
        result = result
            .replace(/,[ \t]{2,}/g, ', ')   // comma followed by extra spaces
            .replace(/[ \t]{2,}/g, ' ')     // multiple spaces → single space (newlines preserved)
            .trim();
        return result;
    };

    const subject = substitute(startup.email_subject || '');
    const body    = substitute(startup.email_body    || '');

    // URLSearchParams encodes spaces as + but mailto: requires %20; newlines
    // in body need to be %0A, which encodeURIComponent produces correctly.
    const subjectEncoded = subject ? encodeURIComponent(subject) : '';
    const bodyEncoded    = body    ? encodeURIComponent(body)    : '';

    const parts = [];
    if (subjectEncoded) parts.push(`subject=${subjectEncoded}`);
    if (bodyEncoded)    parts.push(`body=${bodyEncoded}`);

    return parts.length > 0
        ? `mailto:${email}?${parts.join('&')}`
        : `mailto:${email}`;
}

// Stages that are collapsed by default
//   - Wishlist: nice-to-track but not active work
//   - Deal Closed + Deal Lost: closed/dead, only relevant for reference
const COLLAPSED_BY_DEFAULT = new Set(['Wishlist', 'Deal Closed', 'Deal Lost']);

// Stages that have a distinct visual treatment (parking lot)
const PARKING_STAGES = new Set(['On Hold']);

// localStorage keys
const STORAGE_STARTUP = 'behold.kanban.selected_startup';
const STORAGE_COLLAPSED = 'behold.kanban.collapsed_stages';

export function DealsView() {
    const queryClient = useQueryClient();
    const { user: currentUser } = useAuth();

    // ---- Startup selection (remembered per browser session) ----
    const [startupId, setStartupId] = useState(() => {
        const saved = localStorage.getItem(STORAGE_STARTUP);
        return saved ? Number(saved) : null;
    });

    useEffect(() => {
        if (startupId) localStorage.setItem(STORAGE_STARTUP, String(startupId));
    }, [startupId]);

    // ---- Which columns are collapsed ----
    // Saved choice (localStorage) merges with current defaults — so if we add
    // a new default-collapsed stage in code, users get it auto-collapsed even
    // if their localStorage from a previous version didn't include it.
    const [collapsed, setCollapsed] = useState(() => {
        try {
            const saved = JSON.parse(localStorage.getItem(STORAGE_COLLAPSED) || 'null');
            if (Array.isArray(saved)) {
                // Track which defaults we've ever shown the user (so we don't keep
                // re-collapsing a stage they intentionally expanded).
                const seenDefaults = new Set(
                    JSON.parse(localStorage.getItem(STORAGE_COLLAPSED + '.seen') || '[]')
                );
                const merged = new Set(saved);
                for (const stage of COLLAPSED_BY_DEFAULT) {
                    if (!seenDefaults.has(stage)) merged.add(stage);
                }
                return merged;
            }
        } catch { /* ignore */ }
        return new Set(COLLAPSED_BY_DEFAULT);
    });

    useEffect(() => {
        localStorage.setItem(STORAGE_COLLAPSED, JSON.stringify([...collapsed]));
        // Track that the user has now seen the current defaults
        localStorage.setItem(
            STORAGE_COLLAPSED + '.seen',
            JSON.stringify([...COLLAPSED_BY_DEFAULT])
        );
    }, [collapsed]);

    const toggleCollapsed = (stage) => {
        setCollapsed(prev => {
            const next = new Set(prev);
            next.has(stage) ? next.delete(stage) : next.add(stage);
            return next;
        });
    };

    // ---- Data fetching ----
    const { data: startups } = useQuery({
        queryKey: ['startups'],
        queryFn: () => api.get('/startups'),
    });

    // True when a client user has exactly one accessible startup.
    // Used to skip the picker screen and hide the "back" control.
    const isSingleStartupClient =
        currentUser?.role === 'client' && !!startups && startups.length === 1;

    // Auto-select for single-startup clients. Corrects both a missing startupId
    // (fresh load) and a stale one (client's access changed since last session).
    useEffect(() => {
        if (isSingleStartupClient && startupId !== startups[0].id) {
            setStartupId(startups[0].id);
        }
    }, [isSingleStartupClient, startupId, startups]);

    const { data: boardData, isLoading, error } = useQuery({
        queryKey: ['board', startupId],
        queryFn: () => api.get(`/deals/board?startup_id=${startupId}`),
        enabled: startupId != null,
    });

    // ---- Optimistic state for drag (so the card doesn't snap back) ----
    const [localBoard, setLocalBoard] = useState(null);

    useEffect(() => {
        if (boardData?.board) setLocalBoard(boardData.board);
    }, [boardData]);

    const stages = boardData?.stages || [];

    // ---- Move mutation (drag-and-drop) ----
    const [moveModal, setMoveModal] = useState(null);  // {dealId, fromStage, toStage, onConfirm}
    const [selectedDeal, setSelectedDeal] = useState(null);  // currently-open detail panel

    // Keep selectedDeal in sync with fresh board data — when the user saves changes,
    // the board re-fetches, and we want the detail panel to reflect the new state
    // without needing to close + reopen.
    useEffect(() => {
        if (!selectedDeal || !boardData) return;
        for (const stageDeals of Object.values(boardData.board || {})) {
            const fresh = stageDeals.find(d => d.id === selectedDeal.id);
            if (fresh) {
                // Only update if something actually changed (avoid render loops)
                if (JSON.stringify(fresh) !== JSON.stringify(selectedDeal)) {
                    setSelectedDeal(fresh);
                }
                return;
            }
        }
        // Deal no longer exists (deleted) — close the panel
        setSelectedDeal(null);
    }, [boardData, selectedDeal]);

    const moveMutation = useMutation({
        mutationFn: ({ id, stage, sort_order, notes }) =>
            api.put(`/deals/${id}/move`, { stage, sort_order, notes }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['board', startupId] }),
        onError: () => queryClient.invalidateQueries({ queryKey: ['board', startupId] }),
    });

    const deleteMutation = useMutation({
        mutationFn: (id) => api.delete(`/deals/${id}`),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['board', startupId] }),
    });

    // ---- Drag handler ----
    const onDragEnd = (result) => {
        if (!result.destination || !localBoard) return;
        const { source, destination, draggableId } = result;
        const dealId = Number(draggableId);
        const fromStage = source.droppableId;
        const toStage   = destination.droppableId;

        if (fromStage === toStage && source.index === destination.index) return;

        // Optimistic update: move the card locally first
        const newBoard = { ...localBoard };
        newBoard[fromStage] = [...(newBoard[fromStage] || [])];
        newBoard[toStage]   = fromStage === toStage ? newBoard[fromStage] : [...(newBoard[toStage] || [])];
        const [moved] = newBoard[fromStage].splice(source.index, 1);
        moved.stage = toStage;
        newBoard[toStage].splice(destination.index, 0, moved);
        setLocalBoard(newBoard);

        // If the stage changed, optionally ask for a note. Same-column re-order is silent.
        if (fromStage !== toStage) {
            setMoveModal({
                dealId,
                investorName: moved.investor_name,
                fromStage,
                toStage,
                sortOrder: destination.index,
            });
        } else {
            // Same column — just save the new sort order silently
            moveMutation.mutate({
                id: dealId,
                stage: toStage,
                sort_order: destination.index,
            });
        }
    };

    // ---- Render ----
    if (!startups) {
        return <div className="kanban-loading"><span className="spinner" /></div>;
    }

    // No startup selected: prompt the user to pick one.
    // Single-startup clients skip this screen entirely — the useEffect above
    // is about to set startupId; show a spinner to avoid flashing the picker.
    if (startupId == null) {
        if (isSingleStartupClient) {
            return <div className="kanban-loading"><span className="spinner" /></div>;
        }
        return (
            <div className="kanban">
                <header className="kanban-topbar">
                    <h1 className="display kanban-title">Deal pipeline</h1>
                </header>
                <div className="kanban-picker-empty">
                    <div className="kanban-picker-inner">
                        <Briefcase size={32} strokeWidth={1.25} className="kanban-picker-icon" />
                        <h2>Choose a portfolio company</h2>
                        <p>Each startup has its own pipeline. Pick one to get started.</p>
                        <div className="kanban-picker-grid">
                            {startups.map(s => (
                                <button key={s.id}
                                        className="kanban-picker-card"
                                        onClick={() => setStartupId(s.id)}>
                                    <span className="kanban-picker-name">{s.name}</span>
                                    {s.sector && <span className="kanban-picker-sector">{s.sector}</span>}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const selectedStartup = startups.find(s => s.id === startupId);

    return (
        <div className="kanban">
            {/* Topbar with startup selector */}
            <header className="kanban-topbar">
                <div className="kanban-topbar-left">
                    <h1 className="display kanban-title">Deal pipeline</h1>
                    {isSingleStartupClient ? (
                        selectedStartup?.name
                            ? <span className="kanban-startup-label">{selectedStartup.name}</span>
                            : null
                    ) : (
                        <select
                            className="kanban-startup-select"
                            value={startupId}
                            onChange={(e) => setStartupId(Number(e.target.value))}
                        >
                            {startups.map(s => (
                                <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                        </select>
                    )}
                    {!isSingleStartupClient && (
                        <button
                            className="kanban-change-startup"
                            onClick={() => setStartupId(null)}
                            title="Back to portfolio overview"
                        >
                            <ArrowLeft size={13} strokeWidth={2}/>
                            All portfolios
                        </button>
                    )}
                </div>
                {selectedStartup && (
                    <div className="kanban-meta">
                        {selectedStartup.sector && <span>{selectedStartup.sector}</span>}
                        {selectedStartup.stage && <>
                            <span className="dot-sep">·</span>
                            <span>{selectedStartup.stage}</span>
                        </>}
                    </div>
                )}
            </header>

            {/* Board */}
            {isLoading && <div className="kanban-loading"><span className="spinner" /></div>}
            {error && <div className="panel-error">Couldn't load the board: {error.message}</div>}

            {localBoard && (
                <DragDropContext onDragEnd={onDragEnd}>
                    <div className="kanban-columns">
                        {stages.map(stage => (
                            <Column
                                key={stage}
                                stage={stage}
                                deals={localBoard[stage] || []}
                                collapsed={collapsed.has(stage)}
                                onToggleCollapse={() => toggleCollapsed(stage)}
                                onDelete={(id) => {
                                    if (confirm('Remove this deal from the pipeline? The investor record itself will not be deleted.')) {
                                        deleteMutation.mutate(id);
                                    }
                                }}
                                onOpenDetail={(deal) => setSelectedDeal(deal)}
                                startup={selectedStartup}
                                currentUser={currentUser}
                            />
                        ))}
                    </div>
                </DragDropContext>
            )}

            {/* Right-side detail panel for a selected deal */}
            {selectedDeal && (
                <DealDetailPanel
                    deal={selectedDeal}
                    onClose={() => setSelectedDeal(null)}
                    onUpdated={() => {
                        queryClient.invalidateQueries({ queryKey: ['board', startupId] });
                    }}
                />
            )}

            {/* Optional note-on-move modal */}
            {moveModal && (
                <MoveNoteModal
                    {...moveModal}
                    onCancel={() => {
                        // Persist the move without a note (we already did the optimistic update)
                        moveMutation.mutate({
                            id: moveModal.dealId,
                            stage: moveModal.toStage,
                            sort_order: moveModal.sortOrder,
                        });
                        setMoveModal(null);
                    }}
                    onSubmit={(note) => {
                        moveMutation.mutate({
                            id: moveModal.dealId,
                            stage: moveModal.toStage,
                            sort_order: moveModal.sortOrder,
                            notes: note,
                        });
                        setMoveModal(null);
                    }}
                />
            )}
        </div>
    );
}

/* ==========================================================================
 * Column
 * ========================================================================== */
function Column({ stage, deals, collapsed, onToggleCollapse, onDelete, onOpenDetail, startup, currentUser }) {
    const isParking = PARKING_STAGES.has(stage);
    const canCollapse = true;  // all columns collapsible

    return (
        <div className={`kanban-column${isParking ? ' is-parking' : ''}${collapsed ? ' is-collapsed' : ''}`}>
            <header className="kanban-column-header" onClick={canCollapse ? onToggleCollapse : undefined}>
                <div className="kanban-column-title">
                    {canCollapse && (
                        collapsed
                            ? <ChevronRight size={14} strokeWidth={2} />
                            : <ChevronDown size={14} strokeWidth={2} />
                    )}
                    <span>{stage}</span>
                </div>
                <span className="kanban-column-count">{deals.length}</span>
            </header>

            {!collapsed && (
                <Droppable droppableId={stage}>
                    {(provided, snapshot) => (
                        <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className={`kanban-column-body${snapshot.isDraggingOver ? ' is-drag-over' : ''}`}
                        >
                            {deals.length === 0 && (
                                <div className="kanban-empty">Drop deals here</div>
                            )}
                            {deals.map((deal, idx) => (
                                <Draggable key={deal.id} draggableId={String(deal.id)} index={idx}>
                                    {(provided, snapshot) => (
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            {...provided.dragHandleProps}
                                            className={`kanban-card${snapshot.isDragging ? ' is-dragging' : ''}`}
                                        >
                                            <DealCard
                                                deal={deal}
                                                onDelete={() => onDelete(deal.id)}
                                                onOpenDetail={() => onOpenDetail(deal)}
                                                startup={startup}
                                                currentUser={currentUser}
                                            />
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            )}
        </div>
    );
}

/* ==========================================================================
 * Deal card (the visible card content)
 * ========================================================================== */
function DealCard({ deal, onDelete, onOpenDetail, startup, currentUser }) {
    const queryClient = useQueryClient();

    // Last activity: prefer last_contact_at (manually set), fallback to updated_at (auto)
    const lastContactDate = deal.last_contact_at || deal.updated_at;
    const isManualContact = !!deal.last_contact_at;

    // Optimistic local state for the non-exclusive toggle
    const [isNonExclusive, setIsNonExclusive] = useState(!!deal.nonExclusive);
    const [isToggling, setIsToggling]         = useState(false);

    const handleLockClick = async (e) => {
        e.stopPropagation();
        const next = !isNonExclusive;
        setIsNonExclusive(next);
        setIsToggling(true);
        try {
            await api.patch(`/deals/${deal.id}/non-exclusive`, { nonExclusive: next });
            queryClient.invalidateQueries({ queryKey: ['board', startup?.id] });
        } catch (err) {
            console.error('Non-exclusive toggle failed:', err);
            setIsNonExclusive(!next);   // revert
        } finally {
            setIsToggling(false);
        }
    };

    return (
        <div className="kanban-card-clickable" onClick={onOpenDetail}>
            <div className="kanban-card-row kanban-card-top">
                <span className="kanban-card-name">{deal.investor_name}</span>
                <div className="kanban-card-top-right">
                    {deal.owner_user_id && (
                        <span className="kanban-card-owner"
                              title={`Owner: ${deal.owner_name || deal.owner_email || 'unknown'}`}>
                            {ownerInitials(deal.owner_name || deal.owner_email)}
                        </span>
                    )}
                    {deal.fit_level && (
                        <span className={`fit-badge fit-${deal.fit_level.toLowerCase().replace(' ', '-')}`}>
                            {deal.fit_level}
                        </span>
                    )}
                </div>
            </div>

            {deal.primary_contact_name && (
                <div className="kanban-card-contact">
                    {deal.primary_contact_email ? (
                        <a href={buildMailtoUrl(deal, startup, currentUser)}
                           className="kanban-card-contact-link"
                           onClick={(e) => e.stopPropagation()}>
                            <Mail size={10} strokeWidth={1.75}/>
                            {deal.primary_contact_name}
                        </a>
                    ) : (
                        <span>{deal.primary_contact_name}</span>
                    )}
                </div>
            )}

            {/* Last contact note — shown more prominently because it's the active state of the deal */}
            {deal.last_contact_note && (
                <div className="kanban-card-contact-note" title={deal.last_contact_note}>
                    <span className="kanban-card-note-label">Last contact</span>
                    {deal.last_contact_note}
                </div>
            )}

            {/* Internal notes — secondary context, more muted */}
            {deal.notes && (
                <div className="kanban-card-internal-note" title={deal.notes}>
                    {deal.notes}
                </div>
            )}

            <div className="kanban-card-row kanban-card-bottom">
                <div className="kanban-card-tags">
                    {deal.expected_ticket && (
                        <span className="kanban-card-ticket mono">{deal.expected_ticket}</span>
                    )}
                    <span className={`kanban-card-stale${isManualContact ? ' is-manual' : ''}`}
                          title={isManualContact
                              ? `Last contact: ${formatRelative(lastContactDate)}${deal.last_contact_note ? ` — ${deal.last_contact_note}` : ''}`
                              : `Last activity: ${formatRelative(lastContactDate)}`}>
                        <Clock size={10} strokeWidth={1.5}/>
                        {formatRelative(lastContactDate)}
                    </span>
                </div>
                <div className="kanban-card-actions">
                    <button
                        className={`kanban-lock-btn${isNonExclusive ? ' is-non-exclusive' : ''}`}
                        onClick={handleLockClick}
                        disabled={isToggling}
                        title={isNonExclusive
                            ? 'Non-exclusive (client-sourced, no success fee) — click to unmark'
                            : 'Exclusive (Behold) — click to mark non-exclusive'}
                    >
                        {isNonExclusive
                            ? <Lock size={11} strokeWidth={1.75}/>
                            : <LockOpen size={11} strokeWidth={1.75}/>}
                    </button>
                    <button
                        className="kanban-card-delete"
                        onClick={(e) => { e.stopPropagation(); onDelete(); }}
                        title="Remove from board"
                    >
                        <Trash2 size={11} strokeWidth={1.75}/>
                    </button>
                </div>
            </div>
        </div>
    );
}

function ownerInitials(s) {
    if (!s) return '?';
    // Strip @domain if it's an email
    const cleaned = s.includes('@') ? s.split('@')[0] : s;
    const parts = cleaned.split(/[\s.]+/).filter(Boolean).slice(0, 2);
    return parts.map(p => p[0]).join('').toUpperCase() || '?';
}

/* ==========================================================================
 * MoveNoteModal — optional note when a card changes stage
 * ========================================================================== */
function MoveNoteModal({ investorName, fromStage, toStage, onCancel, onSubmit }) {
    const [note, setNote] = useState('');

    return (
        <div className="modal-overlay" onClick={onCancel}>
            <div className="modal-dialog move-note-dialog" onClick={(e) => e.stopPropagation()}>
                <header className="modal-header">
                    <div>
                        <h2 className="modal-title">Add a note?</h2>
                        <p className="modal-subtitle">
                            {investorName}: <strong>{fromStage}</strong> → <strong>{toStage}</strong>
                        </p>
                    </div>
                </header>
                <form className="modal-body" onSubmit={(e) => { e.preventDefault(); onSubmit(note.trim()); }}>
                    <label className="detail-field">
                        <span className="detail-field-label">Why this move? (optional)</span>
                        <textarea
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            className="detail-field-input"
                            rows={3}
                            placeholder="Skip this to just move the card."
                            autoFocus
                        />
                    </label>
                    <div className="modal-actions">
                        <button type="button" className="btn-ghost" onClick={onCancel}>
                            <X size={14} strokeWidth={2}/> Skip note
                        </button>
                        <button type="submit" className="btn-save" disabled={!note.trim()}>
                            <Check size={14} strokeWidth={2}/> Save with note
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

/* ========================================================================== */
function formatRelative(iso) {
    if (!iso) return '—';
    const d = new Date(iso);
    const diffDays = Math.floor((Date.now() - d) / 86_400_000);
    if (diffDays < 1) return 'today';
    if (diffDays === 1) return '1d';
    if (diffDays < 30) return `${diffDays}d`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo`;
    return `${Math.floor(diffDays / 365)}y`;
}

/* ==========================================================================
 * DealDetailPanel — slide-in side panel showing full deal info
 * ========================================================================== */
function DealDetailPanel({ deal, onClose, onUpdated }) {
    const queryClient = useQueryClient();
    const [editing, setEditing] = useState(false);
    const [form, setForm] = useState({
        owner_user_id:     deal.owner_user_id || '',
        last_contact_at:   deal.last_contact_at ? deal.last_contact_at.slice(0, 10) : '',
        last_contact_note: deal.last_contact_note || '',
        notes:             deal.notes || '',
    });

    const { data: users } = useQuery({
        queryKey: ['users'],
        queryFn: () => api.get('/auth/users'),
    });

    const updateMutation = useMutation({
        mutationFn: (updates) => api.put(`/deals/${deal.id}`, updates),
        onSuccess: () => {
            onUpdated?.();
            setEditing(false);
        },
    });

    const handleSave = () => {
        const updates = {};
        if (form.owner_user_id !== (deal.owner_user_id || '')) {
            updates.owner_user_id = form.owner_user_id ? Number(form.owner_user_id) : null;
        }

        // If the user typed a note but didn't pick a date, default to today.
        // This prevents the common case where a saved note has no date and
        // looks orphaned in the read view.
        let effectiveDate = form.last_contact_at;
        if (form.last_contact_note.trim() && !effectiveDate && !deal.last_contact_at) {
            const today = new Date();
            effectiveDate = today.toISOString().slice(0, 10);
        }

        if (effectiveDate !== (deal.last_contact_at ? deal.last_contact_at.slice(0, 10) : '')) {
            updates.last_contact_at = effectiveDate
                ? new Date(effectiveDate).toISOString()
                : null;
        }
        if (form.last_contact_note !== (deal.last_contact_note || '')) {
            updates.last_contact_note = form.last_contact_note || null;
        }
        if (form.notes !== (deal.notes || '')) {
            updates.notes = form.notes || null;
        }
        if (Object.keys(updates).length === 0) {
            setEditing(false);
            return;
        }
        updateMutation.mutate(updates);
    };

    return (
        <div className="deal-panel-overlay" onClick={onClose}>
            <aside className="deal-panel" onClick={(e) => e.stopPropagation()}>
                <header className="deal-panel-header">
                    <div>
                        <h2 className="deal-panel-title">{deal.investor_name}</h2>
                        <p className="deal-panel-sub">
                            {deal.startup_name} · <span className="deal-panel-stage">{deal.stage}</span>
                        </p>
                    </div>
                    <button className="modal-close" onClick={onClose}>
                        <X size={16} strokeWidth={2}/>
                    </button>
                </header>

                <div className="deal-panel-body">
                    {/* Quick facts */}
                    <section className="deal-panel-section">
                        <h3>Investor</h3>
                        <dl className="deal-panel-grid">
                            {deal.fit_level && (
                                <>
                                    <dt>Fit level</dt>
                                    <dd>
                                        <span className={`fit-badge fit-${deal.fit_level.toLowerCase().replace(' ', '-')}`}>
                                            {deal.fit_level}
                                        </span>
                                    </dd>
                                </>
                            )}
                            {deal.country && (<><dt>Country</dt><dd>{deal.country}</dd></>)}
                            {deal.expected_ticket && (<><dt>Ticket sweetspot</dt><dd className="mono">{deal.expected_ticket}</dd></>)}
                            {deal.investor_status && (<><dt>Status</dt><dd>{deal.investor_status}</dd></>)}
                        </dl>
                        {deal.primary_contact_name && (
                            <p className="deal-panel-contact">
                                <strong>Primary contact:</strong>{' '}
                                {deal.primary_contact_email ? (
                                    <a href={`mailto:${deal.primary_contact_email}`}>
                                        {deal.primary_contact_name}
                                    </a>
                                ) : deal.primary_contact_name}
                            </p>
                        )}
                    </section>

                    {/* Owner + last contact (editable) */}
                    <section className="deal-panel-section">
                        <div className="deal-panel-section-header">
                            <h3>Deal management</h3>
                            {!editing ? (
                                <button className="btn-ghost btn-ghost-small"
                                        onClick={() => setEditing(true)}>
                                    Edit
                                </button>
                            ) : (
                                <div style={{ display: 'flex', gap: 6 }}>
                                    <button className="btn-ghost btn-ghost-small"
                                            onClick={() => setEditing(false)}
                                            disabled={updateMutation.isPending}>
                                        Cancel
                                    </button>
                                    <button className="btn-save btn-ghost-small"
                                            onClick={handleSave}
                                            disabled={updateMutation.isPending}>
                                        {updateMutation.isPending
                                            ? <span className="spinner"/>
                                            : <><Check size={12} strokeWidth={2}/> Save</>}
                                    </button>
                                </div>
                            )}
                        </div>

                        {editing ? (
                            <div className="deal-panel-edit">
                                <label className="detail-field">
                                    <span className="detail-field-label">Deal owner</span>
                                    <select className="detail-field-input"
                                            value={form.owner_user_id}
                                            onChange={(e) => setForm(f => ({ ...f, owner_user_id: e.target.value }))}>
                                        <option value="">— No owner —</option>
                                        {(users || [])
                                            .filter(u => u.role !== 'client' && u.is_active)
                                            .map(u => (
                                                <option key={u.id} value={u.id}>
                                                    {u.name || u.email}
                                                </option>
                                            ))}
                                    </select>
                                </label>
                                <label className="detail-field">
                                    <span className="detail-field-label">Last contact date</span>
                                    <input type="date" className="detail-field-input"
                                           value={form.last_contact_at}
                                           onChange={(e) => setForm(f => ({ ...f, last_contact_at: e.target.value }))}/>
                                </label>
                                <label className="detail-field">
                                    <span className="detail-field-label">Last contact note</span>
                                    <textarea rows={2} className="detail-field-input"
                                              value={form.last_contact_note}
                                              onChange={(e) => setForm(f => ({ ...f, last_contact_note: e.target.value }))}
                                              placeholder="What happened? Email sent, call had, meeting set..."/>
                                </label>
                                <label className="detail-field">
                                    <span className="detail-field-label">Internal notes</span>
                                    <textarea rows={3} className="detail-field-input"
                                              value={form.notes}
                                              onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                                              placeholder="Any other context worth keeping..."/>
                                </label>
                                {updateMutation.isError && (
                                    <div className="panel-error">{updateMutation.error.message}</div>
                                )}
                            </div>
                        ) : (
                            <dl className="deal-panel-grid">
                                <dt>Owner</dt>
                                <dd>{deal.owner_name || deal.owner_email || <span className="muted">— Unassigned —</span>}</dd>

                                <dt>Last contact</dt>
                                <dd>
                                    {deal.last_contact_at
                                        ? new Date(deal.last_contact_at).toLocaleDateString()
                                        : <span className="muted">— No manual date logged —</span>}
                                </dd>

                                {deal.last_contact_note && (
                                    <>
                                        <dt>Contact note</dt>
                                        <dd className="deal-panel-note-text">{deal.last_contact_note}</dd>
                                    </>
                                )}

                                <dt>Last activity</dt>
                                <dd>
                                    {deal.updated_at
                                        ? formatRelative(deal.updated_at) + ' ago'
                                        : <span className="muted">—</span>}
                                </dd>

                                {deal.notes && (
                                    <>
                                        <dt>Notes</dt>
                                        <dd className="deal-panel-note-text">{deal.notes}</dd>
                                    </>
                                )}
                            </dl>
                        )}
                    </section>
                </div>
            </aside>
        </div>
    );
}
